import React, {useEffect, useState} from "react";
import InvoiceAdditionalExpense from "@/models/server/InvoiceAdditionalExpense";
import AdditionalExpense from "@/models/server/AdditionalExpense";
import Invoice from "@/models/server/Invoice";
import {ExpenseType, InvoiceStatus, InvoiceType, ResourceItemType, UserRole} from "@/models/Enums";
import InvoiceExpense from "@/models/server/InvoiceExpense";
import Localizer from "@/localization/Localizer";
import DeviceContract from "@/models/server/DeviceContract";
import DeviceInfo from "@/models/server/DeviceInfo";
import Report from "@/pages/Models/Report";
import ServiceReport from "@/pages/Models/ServiceReport";
import ServiceInvoice from "@/models/server/ServiceInvoice";
import ReturnInspectionInvoice from "@/models/server/ReturnInspectionInvoice";
import Expense from "@/models/server/Expense";
import ReportDefinition from "@/pages/Models/ReportDefinition";
import {ch, PageRouteProvider} from "@renta-apps/athenaeum-react-common";
import User from "@/models/server/User";
import FindDeviceResponse from "@/models/server/responses/FindDeviceResponse";
import RentaToolsController from "@/pages/RentaToolsController";
import ReturnInspectionController from "@/pages/ReturnInspectionController";
import PageDefinitions from "@/providers/PageDefinitions";
import SaveInvoiceRequest from "@/models/server/requests/SaveInvoiceRequest";
import HttpClient from "@/common/HttpClient";
import SaveInvoiceResponse from "@/models/server/responses/SaveInvoiceResponse";
import EndpointPaths from "@/common/EndpointPaths";
import ProcessReturnInspectionInvoiceRequest from "@/models/server/requests/ProcessReturnInspectionInvoiceRequest";
import ProcessReturnInspectionInvoiceResponse from "@/models/server/responses/ProcessReturnInspectionInvoiceResponse";
import ProcessServiceInvoiceRequest from "@/models/server/requests/ProcessServiceInvoiceRequest";
import ProcessServiceInvoiceResponse from "@/models/server/responses/ProcessServiceInvoiceResponse";
import styles from "@/pages/InvoiceDetailsPage/InvoiceDetailsPage.module.scss";
import ReportDefinitionItem from "@/pages/Models/ReportDefinitionItem";
import {ButtonType, Checkbox, Dropdown, DropdownRequiredType, InlineType, Link, NumberWidget, PageContainer, PageHeader} from "@renta-apps/athenaeum-react-components";
import rentaToolsStyles from "@/pages/RentaTools.module.scss";
import ToolsUtility from "@/helpers/ToolsUtility";
import {Utility} from "@renta-apps/athenaeum-toolkit";
import ArsenalButton from "@/components/ArsenalButton/ArsenalButton";
import ArsenalPageRow from "@/components/ArsenalPageRow/ArsenalPageRow";

export interface IInvoiceDetailsProps {
    invoiceType: InvoiceType;
    invoiceId: string | null;
}

const InvoiceDetails = (props: IInvoiceDetailsProps) => {
    const [expenses, setExpenses] = useState<Expense[]>([]);
    const [additionalExpenses, setAdditionalExpenses] = useState<AdditionalExpense[]>([]);
    const [invoice, setInvoice] = useState<Invoice | ServiceInvoice | ReturnInspectionInvoice | null>(null);
    const [device, setDevice] = useState<DeviceInfo>();
    const [invoiceOtherResources, setInvoiceOtherResources] = useState<boolean>(false);
    const [returnInspectionReportDefinition, setReturnInspectionReportDefinition] = useState<ReportDefinition | null>(null);
    const [deviceContracts, setDeviceContracts] = useState<DeviceContract[]>([]);
    const [manualFuelStep, setManualFuelStep] = useState<number | null>(null);
    const [selectedContract, setSelectedContract] = useState<DeviceContract | null>(null);
    const [initialInvoicedAdditionalExpenses, setInitialInvoicedAdditionalExpenses] = useState<InvoiceAdditionalExpense[]>([]);

    useEffect(() => {
        const isManualInvoice = props.invoiceType === InvoiceType.ManualInvoices || props.invoiceType === InvoiceType.SkippedInspectionInvoice;

        Promise.all([RentaToolsController.listExpensesAsync(), fetchInvoiceAsync(), RentaToolsController.getAdditionalExpensesAsync(true)])
            .then(results => {
                const [fetchedExpenses, fetchedInvoice, fetchedAdditionalExpenses] = results;

                setExpenses(fetchedExpenses);

                if (!fetchedInvoice) {
                    return;
                }

                if (fetchedInvoice.device) {
                    setDevice(fetchedInvoice.device);
                }
                else if (RentaToolsController.device) {
                    setDevice(RentaToolsController.device);
                }

                if (fetchedInvoice.deviceContract != null && !deviceContracts.some(c => c.id == fetchedInvoice.deviceContract?.id)) {
                    const contracts: DeviceContract[] = deviceContracts;
                    contracts.push(fetchedInvoice.deviceContract);
                    setDeviceContracts(contracts);
                }

                const inspectionInvoice = fetchedInvoice as ReturnInspectionInvoice;
                if (inspectionInvoice) {
                    const initialAdditionalExpenses: InvoiceAdditionalExpense[] = Utility.clone(inspectionInvoice.additionalExpenses);
                    setInitialInvoicedAdditionalExpenses(initialAdditionalExpenses);
                }

                // Handle additional expenses
                const filteredAdditionalExpenses: AdditionalExpense[] = fetchedAdditionalExpenses
                        .where(item => (!item.isDeleted) && (!isManualInvoice || item.isInvoiceable));

                setAdditionalExpenses(filteredAdditionalExpenses);

                // Set additional expenses to invoiced automatically if applicable
                fetchedInvoice.additionalExpenses.forEach(exp => {
                    const matchingExpense = fetchedAdditionalExpenses.find((ex) => ex.productExternalId == exp.externalId);
                    if (fetchedInvoice.status == InvoiceStatus.Pending && exp.value > 0 && matchingExpense?.isInvoiceable) {
                        exp.invoiced = true;
                        exp.modified = true;
                    }
                })

                // Autoselect invoice expenses if applicable
                fetchedInvoice.invoiceExpenses.forEach(invoiceExpense => {
                    const matchingExpense = fetchedExpenses.firstOrDefault(expense => expense.id == invoiceExpense.expenseId);
                    if (fetchedInvoice.status == InvoiceStatus.Pending && invoiceExpense.amount != null && invoiceExpense.amount > 0 && matchingExpense?.isInvoiceable) {
                        invoiceExpense.modified = true;
                        invoiceExpense.shouldInvoice = true;
                    }
                })

                setInvoice({... fetchedInvoice})
            })

        if (isManualInvoice) {
            RentaToolsController.getManualFuelStep().then(result => {
                setManualFuelStep(result);
            })
        }
    }, [])

    useEffect(() => {
        if (!device) {
            return;
        }

        RentaToolsController.getDeviceContractsAsync(device!.id, false).then(results => {
            const combined: DeviceContract[] = deviceContracts.concat(results);
            setDeviceContracts(combined);
        })
    }, [device])

    const fetchInvoiceAsync = async (): Promise<Invoice | ServiceInvoice | ReturnInspectionInvoice | null> => {
        let result: Invoice | ServiceInvoice | ReturnInspectionInvoice | null = null;
        let definition: ReportDefinition | null = null;
        if (!props.invoiceId) {
            result = new ReturnInspectionInvoice();
        } else {
            switch (props.invoiceType) {
                case InvoiceType.ManualInvoices:
                    result = await HttpClient.postAsyncWithoutErrorHandling(EndpointPaths.InvoicePaths.GetInvoice, props.invoiceId);
                    break;
                case InvoiceType.ReturnInspectionInvoice:
                    result = await HttpClient.postAsyncWithoutErrorHandling(EndpointPaths.InvoicePaths.GetReturnInspectionInvoice, props.invoiceId) as ReturnInspectionInvoice;
                    const inspectionInvoice = result as ReturnInspectionInvoice;
                    definition = await HttpClient.postAsyncWithoutErrorHandling(EndpointPaths.ReportDefinition.FindReportDefinition, inspectionInvoice.returnInspectionReport.reportDefinitionId);
                    break;
                case InvoiceType.ServiceInvoice:
                    result = await HttpClient.postAsyncWithoutErrorHandling(EndpointPaths.InvoicePaths.GetServiceInvoice, props.invoiceId) as ServiceInvoice;
                    break;
                case InvoiceType.SkippedInspectionInvoice:
                    result = await HttpClient.postAsyncWithoutErrorHandling(EndpointPaths.InvoicePaths.GetSkippedInvoice, props.invoiceId);
                    break;
                default:
                    throw new Error("unknown type of invoice");
            }
        }

        setReturnInspectionReportDefinition(definition)

        return result;
    }


    const readonly = (): boolean => {
        return invoice == null || invoice.status !== InvoiceStatus.Pending || invoice.approved == true || invoice.declined == true;
    }

    const deviceContract = () : DeviceContract | null => {
        if (selectedContract) {
            return selectedContract;
        }

        return invoice?.deviceContract ?? null;
    }

    const setAdditionalExpenseValueAsync = async (invoiceAdditionalExpense: InvoiceAdditionalExpense, value: number): Promise<void> => {
        const modifiedInvoice = invoice;

        const matching = modifiedInvoice?.additionalExpenses.firstOrDefault(exp => exp.id == invoiceAdditionalExpense.id);

        if (!matching || !modifiedInvoice) {
            return;
        }

        matching.value = value;
        matching.modified = true;

        if (!manualInvoice()) {
            const matchingExpense = additionalExpenses.firstOrDefault(exp => exp.productExternalId == invoiceAdditionalExpense.externalId);
            matching.invoiced = isAdditionalExpenseInvoiceable(invoiceAdditionalExpense, matchingExpense);
        }

        setInvoice({...modifiedInvoice});
    }

    const noContractSelected = (): boolean => {
        // Has no selected contract, or device contracts collection is empty.
        const selectedInvoiceId = selectedDeviceContractIdForManualInvoice();
        return !selectedInvoiceId;
    }

    const fuelStep = (): number | null => {
        return manualInvoice()
                ? manualFuelStep
                : null;
    }

    const possibleExpenseTypes = (invoiceExpense: InvoiceExpense): Expense[] => {
        if (!invoiceExpense.expenseType) {
            return [];
        }

        return applicableExpenses(invoiceExpense.expenseType);
    }

    const redirectAsync = async (): Promise<void> => {
        isTechnician()
                ? await PageRouteProvider.redirectAsync(PageDefinitions.dashboardRoute)
                : await PageRouteProvider.redirectAsync(PageDefinitions.invoicesRoute);
    }

    const toDevicePage = async (): Promise<void> => {
        if (!device) {
            return;
        }

        await RentaToolsController.searchDeviceAsync(device.externalId);
    }

    const processManualInvoiceAsync = async (approve: boolean, decline: boolean): Promise<void> => {
        if (!invoice) {
            return;
        }

        const request: SaveInvoiceRequest = {
            invoiceId: isNewInvoice() ? null : invoice.id,
            approve: approve,
            reject: decline,
            force: false,
            expenses: invoice.invoiceExpenses,
            comment: invoice.comment,
            additionalExpenses: invoice.additionalExpenses,
            deviceContractExternalId: invoice.deviceContractExternalId,
            deviceId: device!.id,
            invoiceType: props.invoiceType,
        }

        let response = await HttpClient.postAsync<SaveInvoiceResponse>(EndpointPaths.InvoicePaths.SaveInvoice, request, 120);

        let skip: boolean = false;

        if (!response.success) {
            return;
        }

        if (response.contractIsClosed) {
            const reopen: boolean = await ch.confirmAsync(Localizer.invoiceDetailsPageAlertReopenContract);

            if (reopen) {
                request.force = true;

                response = await HttpClient.postAsync(EndpointPaths.InvoicePaths.SaveInvoice, request, 120);
            } else {
                skip = true;
            }
        }

        if (!skip && response.success) {
            await redirectAsync();
            await ch.alertMessageAsync(Localizer.invoiceDetailsPageInvoiceSend, true);
        }
    }

    const processReturnInspectionInvoiceAsync = async (approve: boolean, decline: boolean, comment: string | null): Promise<void> => {
        if (!invoice) {
            return;
        }

        const request: ProcessReturnInspectionInvoiceRequest = {
            returnInspectionInvoiceId: invoice!.id,
            approve: approve,
            decline: decline,
            comment: comment,
            expenses: invoice!.invoiceExpenses,
            additionalExpenses: invoice!.additionalExpenses,
            force: false,
            invoiceType: props.invoiceType
        };

        let response: ProcessReturnInspectionInvoiceResponse = await HttpClient.postAsync(EndpointPaths.InvoicePaths.ProcessReturnInspectionInvoice, request, 120);

        let resultInvoice: ReturnInspectionInvoice = response.invoice!;

        let skip: boolean = false;

        if (!response.success) {
            return;
        }

        if (response.contractIsClosed) {
            const reopen: boolean = await ch.confirmAsync(Localizer.invoiceDetailsPageAlertReopenContract);

            if (reopen) {
                request.force = true;

                response = await HttpClient.postAsync(EndpointPaths.InvoicePaths.ProcessReturnInspectionInvoice, request, 120);

                resultInvoice = response.invoice!;
            } else {
                skip = true;
            }
        }

        setInvoice(resultInvoice)

        if (!skip) {
            await redirectAsync();

            const message: string = (approve)
                    ? Localizer.invoiceDetailsPageInvoiceSend
                    : Localizer.invoiceDetailsPageAlertDeclined;

            await ch.alertMessageAsync(message, true);
        }
    }

    const processServiceInvoiceAsync = async (approve: boolean, decline: boolean, comment: string | null): Promise<void> => {
        if (!invoice) {
            return;
        }

        const request = {
            serviceReportInvoiceId: invoice.id,
            approve: approve,
            decline: decline,
            comment: comment,
            additionalExpenses: invoice.additionalExpenses,
            force: false,
            invoiceType: props.invoiceType
        } as ProcessServiceInvoiceRequest;

        let response: ProcessServiceInvoiceResponse = await HttpClient.postAsync(EndpointPaths.InvoicePaths.ProcessServiceInvoice, request, 120);

        let resultInvoice: ServiceInvoice = response.invoice!;

        let skip: boolean = false;

        if (response.success) {

            if (response.contractIsClosed) {
                const reopen: boolean = await ch.confirmAsync(Localizer.invoiceDetailsPageAlertReopenContractForService);

                if (reopen) {
                    request.force = true;

                    response = await HttpClient.postAsync(EndpointPaths.InvoicePaths.ProcessServiceInvoice, request, 120);

                    resultInvoice = response.invoice!;
                } else {
                    skip = true;
                }
            }

            setInvoice(resultInvoice);

            if (!skip) {
                await redirectAsync();

                const message: string = (approve)
                        ? Localizer.invoiceDetailsPageInvoiceSend
                        : Localizer.invoiceDetailsPageAlertDeclined;

                await ch.alertMessageAsync(message, true);
            }
        }
    }

    const returnBackAsync = async (): Promise<void> => {
        await PageRouteProvider.back();
    }

    const previewReturnInspectionAsync = async (step?: number | null): Promise<void> => {
        const inspectionReport = report();

        if (!inspectionReport || inspectionReport.skipped || !hasData() || !device) {
            return;
        }

        const response: FindDeviceResponse = await RentaToolsController.findDeviceByIdAsync(device.id);

        if (response.device) {
            RentaToolsController.device = response.device;

            if (returnInspectionInvoice()) {
                RentaToolsController.reportPreview = report() as Report;

                if (step != null) {
                    await ReturnInspectionController.openStepAsync(step);
                } else {
                    await PageRouteProvider.redirectAsync(PageDefinitions.reportPreviewRoute);
                }
            } else if (serviceInvoice()) {
                RentaToolsController.servicePreview = report() as ServiceReport;

                await PageRouteProvider.redirectAsync(PageDefinitions.servicePreviewRoute);
            }
        }
    }


    const setAdditionalExpenseInvoicedAsync = async (invoiceAdditionalExpense: InvoiceAdditionalExpense, invoiced: boolean): Promise<void> => {
        if (!invoice) {
            return;
        }

        const modifiedInvoice = invoice;
        const match = modifiedInvoice.additionalExpenses.firstOrDefault(exp => exp.id == invoiceAdditionalExpense.id);
        if (match) {
            invoiceAdditionalExpense.modified = true;
            invoiceAdditionalExpense.invoiced = invoiced;
        }

        setInvoice({...modifiedInvoice});
    }

    const selectedExpenseType = (invoiceExpense: InvoiceExpense): string | null => {
        return invoiceExpense.expenseId;
    }

    const isNewInvoice = (): boolean => {
        return invoice?.id.length === 0;
    }

    const canApprove = (): boolean => {
        if (invoice?.status != InvoiceStatus.Pending) {
            return false;
        }

        const hasInvoiceExpenses = invoice.invoiceExpenses.length > 0;

        let expensesOk = invoice.invoiceExpenses.some(invoiceExpense => {
            return InvoiceExpense.readyForInvoicing(invoiceExpense, expenses);
        });

        const hasAdditionalExpenses: boolean = invoicedAdditionalExpenses().length > 0;
        const hasAdditionalExpensesToInvoice: boolean = invoicedAdditionalExpenses().some(invoiceExpense => {
            return InvoiceAdditionalExpense.readyForApproval(invoiceExpense);
        });

        return (hasInvoiceExpenses && expensesOk) || (hasAdditionalExpenses && hasAdditionalExpensesToInvoice);
    }

    const canReject = (): boolean => {
        if (invoice?.status != InvoiceStatus.Pending) {
            return false;
        }

        return !isNewInvoice();
    }

    const statusLabel = (): string => {
        if (isNewInvoice()) {
            return Localizer.invoiceDetailsPageNewInvoice;
        }
        return (invoice?.approved)
                ? Localizer.invoiceDetailsPageInvoiceStatusApproved
                : (invoice?.declined)
                        ? Localizer.invoiceDetailsPageInvoiceStatusDeclined
                        : Localizer.invoiceDetailsPageInvoiceStatusPending;
    }

    const isExpenseInvoiceable = (expense: InvoiceExpense): boolean => {
        const matchingExpense = expenses.firstOrDefault(exp => exp.id == expense.expenseId);

        return (matchingExpense?.isInvoiceable ?? false) && (expense.amount != null && expense.amount > 0);
    }

    const isPending = (): boolean => {
        return invoice?.status === InvoiceStatus.Pending;
    }

    const serviceInvoice = (): boolean => {
        return props.invoiceType == InvoiceType.ServiceInvoice;
    }

    const returnInspectionInvoice = (): boolean => {
        return props.invoiceType == InvoiceType.ReturnInspectionInvoice;
    }

    const manualInvoice = (): boolean => {
        return props.invoiceType === InvoiceType.ManualInvoices || props.invoiceType === InvoiceType.SkippedInspectionInvoice;
    }

    const isAdditionalExpenseInvoiceable = (invoicedExpense: InvoiceAdditionalExpense, expenseType: AdditionalExpense | null): boolean => {
        return (expenseType?.isInvoiceable ?? false) && invoicedExpense.value > 0;
    }

    const setContractIdAsync = async (contract: DeviceContract): Promise<void> => {
        if (!contract || !invoice) {
            return;
        }

        const modified = invoice;

        modified.deviceContract = contract;
        modified.deviceContractExternalId = contract.contractExternalId;

        setInvoice({...modified});
        setSelectedContract(contract);
    }

    const setExpenseAmountAsync = async (expenseId: string, amount: number): Promise<void> => {
        if (!invoice) {
            return;
        }

        const modifiedInvoice = invoice;
        const matching = modifiedInvoice?.invoiceExpenses.firstOrDefault(exp => exp.expenseId == expenseId);

        if (!matching) {
            return;
        }

        matching.modified = true;
        matching.amount = amount;

        if (!manualInvoice()) {
            matching.shouldInvoice = isExpenseInvoiceable(matching);
        }

        setInvoice({...modifiedInvoice});
    }

    const setExpenseTypeAsync = async (existingInvoiceExpense: InvoiceExpense, newExpense: Expense): Promise<void> => {
        if (!invoice) {
            return;
        }

        const modifiedInvoice = invoice;
        const match = modifiedInvoice.invoiceExpenses.firstOrDefault((exp => exp.expenseId === existingInvoiceExpense.expenseId));

        if (!match) {
            return;
        }

        match.expenseId = newExpense.id;
        match.externalId = newExpense.productExternalId;
        match.name = newExpense.name;
        match.expenseType = newExpense.expenseType;
        match.modified = true;

        if (!manualInvoice()) {
            match.shouldInvoice = isExpenseInvoiceable(match);
        }

        setInvoice({...modifiedInvoice});
    }

    const setShouldInvoiceExpense = async (expenseId: string, shouldInvoice: boolean): Promise<void> => {
        if (!invoice) {
            return;
        }

        const modifiedInvoice = invoice;
        const match = modifiedInvoice.invoiceExpenses.firstOrDefault(exp => exp.expenseId == expenseId);

        if (match) {
            match.modified = true;
            match.shouldInvoice = shouldInvoice;
        }

        setInvoice({...modifiedInvoice});
    }

    const invoicedAdditionalExpenses = (): InvoiceAdditionalExpense[] => {
        let additionalExpenses: InvoiceAdditionalExpense[] = invoice?.additionalExpenses ?? [];

        if (additionalExpenses.length > 0) {
            additionalExpenses.order(item => item.returnInspectionStepNumber);
        }

        return additionalExpenses;
    }

    const isTechnician = (): boolean => {
        return ch.getUser<User>().role === UserRole.Technician;
    }

    const setInvoiceOtherResourcesAsync = async (value: boolean): Promise<void> => {
        setInvoiceOtherResources(value);
    }

    const processInvoiceAsync = async (approve: boolean, decline: boolean, comment: string | null): Promise<void> => {
        switch (props.invoiceType) {
            case InvoiceType.ManualInvoices:
            case InvoiceType.SkippedInspectionInvoice:
                await processManualInvoiceAsync(approve, decline);
                break;

            case InvoiceType.ReturnInspectionInvoice:
                await processReturnInspectionInvoiceAsync(approve, decline, comment);
                break;

            case InvoiceType.ServiceInvoice:
                await processServiceInvoiceAsync(approve, decline, comment);
                break;

            default:
                throw Error(`Unknown invoice type \"${props.invoiceType}\".`);
        }
    }

    const addExpenseToInvoiceAsync = async (newExpense: Expense): Promise<void> => {
        if (!invoice) {
            return;
        }

        const modifiedInvoice = invoice;

        let invoiceExpense = new InvoiceExpense();
        invoiceExpense.expenseType = newExpense.expenseType;
        invoiceExpense.expenseId = newExpense.id;
        invoiceExpense.name = newExpense.name;
        invoiceExpense.amount = 0;
        invoiceExpense.invoiceId = invoice.id;
        invoiceExpense.status = InvoiceStatus.Pending;
        invoiceExpense.externalId = newExpense.productExternalId;
        invoiceExpense.shouldInvoice = true;

        modifiedInvoice.invoiceExpenses.push(invoiceExpense);

        setInvoice({...modifiedInvoice});
    }

    const applicableExpenses = (type: ExpenseType): Expense[] => {
        if (manualInvoice()) {
            return expenses.where(expense => expense.expenseType == type) ?? [];
        }

        if (!returnInspectionReportDefinition || !returnInspectionReportDefinition.items || returnInspectionReportDefinition.items.length <= 0) {
            return [];
        }

        let resourceType: ResourceItemType;
        switch (type) {
            case ExpenseType.Fueling:
                resourceType = ResourceItemType.Fueling;
                break;
            case ExpenseType.AdBlue:
                resourceType = ResourceItemType.AdBlue;
                break;
            case ExpenseType.Washing:
                resourceType = ResourceItemType.Washing;
                break;
            default:
                throw new Error(`Type: ${type} not supported in InvoiceDetailsPage`);
        }

        const expenseIds = returnInspectionReportDefinition
                .items
                .filter(item => (item.resourceType == resourceType) && (item.valueTypeIds != null))
                .selectMany(item => item.valueTypeIds!);

        return expenses.where(exp => expenseIds.includes(exp.id));
    }

    const getInvoiceExpenseAmount = (invoiceExpense: InvoiceExpense): number => {
        const match = invoice?.invoiceExpenses.firstOrDefault(exp => exp.expenseId == invoiceExpense.expenseId);

        if (!match) {
            return 0;
        }

        return match.amount ?? 0;
    }

    const getInitialAdditionalExpenseAmountDetails = (invoicedExpense: InvoiceAdditionalExpense, format: string): string => {
        const initialInvoicedExpense: InvoiceAdditionalExpense | null = initialInvoicedAdditionalExpenses
                .firstOrDefault(item => item.id == invoicedExpense.id);

        return (initialInvoicedExpense != null)
                ? `{0:${format}} ({1:ExpenseUnit})`.format(initialInvoicedExpense.value, initialInvoicedExpense.unit)
                : "";
    }

    const getAdditionalExpenseName = (invoicedExpense: InvoiceAdditionalExpense): string => {
        return Localizer.invoiceDetailsPageAdditionalExpenseName.format(invoicedExpense.name, invoicedExpense.externalId);
    }

    const report = (): Report | ServiceReport | null => {
        return (invoice)
                ? (serviceInvoice())
                        ? (invoice as ServiceInvoice).serviceReport
                        : (invoice as ReturnInspectionInvoice).returnInspectionReport
                : null;
    }

    const additionalExpenseInvoiced = (invoicedExpense: InvoiceAdditionalExpense): boolean => {
        const expenseType: AdditionalExpense | null = additionalExpenses
                .firstOrDefault(item => (item.id == invoicedExpense.typeId));

        if (invoicedExpense.modified || invoice?.approved == true || invoice?.status == InvoiceStatus.Invoiced) {
            return invoicedExpense.invoiced;
        }

        return invoicedExpense.invoiced || (isPending() && isAdditionalExpenseInvoiceable(invoicedExpense, expenseType));
    }

    const invoiceExpenseSelected = (invoiceExpense: InvoiceExpense): boolean => {
        if (manualInvoice()) {
            return true;
        }

        if (invoice?.declined == true || invoice?.status == InvoiceStatus.Declined) {
            return false;
        }

        if (invoice?.approved == true || invoice?.status == InvoiceStatus.Invoiced) {
            return invoiceExpense.status === InvoiceStatus.Invoiced;
        }

        if (invoiceExpense.modified) {
            return invoiceExpense.shouldInvoice;
        }

        return invoiceExpense.status === InvoiceStatus.Invoiced || invoiceExpense.shouldInvoice || isExpenseInvoiceable(invoiceExpense);
    }

    const selectedDeviceContractIdForManualInvoice = (): string | null => {
        const invoiceContract = deviceContract();
        if (invoiceContract) {
            return invoiceContract.contractExternalId;
        }

        if (device) {
            const contractId = !!device.currentContractExternalId
                    ? device.currentContractExternalId
                    : device.lastContractExternalId;

            if (contractId) {
                return contractId;
            }
        }

        return invoice ? invoice.deviceContractExternalId : null;
    }

    const getExpenseLabel = (invoiceExpense: InvoiceExpense): string => {
        switch (invoiceExpense.expenseType) {
            case ExpenseType.Washing:
                return Localizer.get(Localizer.invoiceDetailsPageNumberInputLabelInvoiceWashing, "({0:0.0} l)".format(invoice?.washingTime ?? 0));
            case ExpenseType.AdBlue:
                return Localizer.get(Localizer.invoiceDetailsPageNumberInputLabelInvoiceAdBlue, "({0:0.0} l)".format(invoice?.adBlueAmount ?? 0));
            case ExpenseType.Fueling:
                return Localizer.get(Localizer.invoiceDetailsPageNumberInputLabelInvoiceFueling, "({0:0.0} l)".format(invoice?.fuelAmount ?? 0));
        }

        return "";
    }

    const getExpenseDropdownLabel = (invoiceExpense: InvoiceExpense): string => {
        switch (invoiceExpense.expenseType) {
            case ExpenseType.Washing:
                return Localizer.fuelingAndWashingWashingType;
            case ExpenseType.AdBlue:
                return Localizer.fuelingAndWashingAdBlue;
            case ExpenseType.Fueling:
                return Localizer.invoiceDetailsPageDropdownFuelType;
            case ExpenseType.Service:
                return Localizer.serviceExpensesPageTitle;
        }

        return Localizer.genericUnknown;
    }

    const hasData = (): boolean => {
        return (invoice != null);
    }

    const renderTitle = (): React.ReactNode => {
        const deviceProductGroupName: string = (device)
                ? device.productGroupName
                : "";
        const deviceType: string = (device)
                ? device.type
                : "";
        const deviceExternalId: string = (device)
                ? device.externalId
                : "";

        return (
                <React.Fragment>

                    <div>
                        <div className={styles.multiTitle}>
                            <span className={styles.deviceProductGroup}>{deviceProductGroupName}</span>
                            <span className={styles.deviceType}>{deviceType}</span>
                            <span>{deviceExternalId}</span>
                        </div>
                    </div>

                </React.Fragment>
        );
    }

    const renderAdditionalExpenses = (invoicedExpenses: InvoiceAdditionalExpense[], index: number): React.ReactNode => {
        const step: number | null = invoicedExpenses
                .map(item => item.returnInspectionStepNumber)
                .firstOrDefault();
        let stepTitle: string | null = null;

        if (returnInspectionInvoice()) {
            if (step != null) {
                const stepItem: ReportDefinitionItem = (invoice as ReturnInspectionInvoice).returnInspectionReport.items[step];

                stepTitle = (stepItem)
                        ? `${Localizer.genericStepNumber.format(step + 1)}: \"${stepItem.name}\"`
                        : Localizer.genericStepNumber.format(step + 1);
            }
        }

        return (
                <div key={index}
                     className={Utility.css(ch.mobile && styles.additionalExpenses)}>

                    {
                            (!manualInvoice() && step != null) &&
                            (
                                    <Link className={styles.additionalExpensesStepLink}
                                          route={() => previewReturnInspectionAsync(step)}>
                                        {stepTitle}
                                    </Link>
                            )
                    }

                    {
                        invoicedExpenses.map(item => renderAdditionalExpense(item, index))
                    }

                    {
                            (ch.desktop) &&
                            (
                                    <hr/>
                            )
                    }

                </div>
        )
    }

    const renderAdditionalExpense = (invoicedExpense: InvoiceAdditionalExpense, index: number): React.ReactNode => {
        const expenseType: AdditionalExpense | null = additionalExpenses
                .firstOrDefault(item => (item.id == invoicedExpense.typeId));

        const step: number = (expenseType != null && !expenseType.isGeneric)
                ? expenseType.step
                : AdditionalExpense.getSuggestedStep(invoicedExpense.unit);

        const format: string = (step != null) && (step % 1 == 0)
                ? "0"
                : "0.00";

        return (
                <>
                    <span className={styles.additionalExpenseName}>{getAdditionalExpenseName(invoicedExpense)}</span>

                    <div className={manualInvoice() ? "" : styles.value}>

                        {
                                (!manualInvoice()) && (
                                        <Checkbox inline
                                                  id={`additionalExpenseInvoiced_${index}`}
                                                  readonly={readonly() || !isAdditionalExpenseInvoiceable(invoicedExpense, expenseType)}
                                                  inlineType={InlineType.Right}
                                                  value={additionalExpenseInvoiced(invoicedExpense)}
                                                  onChange={(_, value) => setAdditionalExpenseInvoicedAsync(invoicedExpense, value)}
                                        />
                                )
                        }

                        <NumberWidget id={`setAdditionalExpenses_${index}`}
                                      className={rentaToolsStyles.arsenalNumberWidget}
                                      label={getInitialAdditionalExpenseAmountDetails(invoicedExpense, format)}
                                      format={format}
                                      step={step}
                                      min={0}
                                      max={expenseType?.max}
                                      readonly={readonly()}
                                      value={invoicedExpense.value || 0}
                                      onChange={(_, value) => setAdditionalExpenseValueAsync(invoicedExpense, value)}
                        />

                    </div>
                </>
        )
    }

    const inspectedAt: string = (report() != null)
            ? (serviceInvoice())
                    ? ToolsUtility.toDateString((report() as ServiceReport).date)
                    : ToolsUtility.toDateString((report() as Report).completedAt)
            : "";

    const groupedAdditionalExpenses: InvoiceAdditionalExpense[][] = invoicedAdditionalExpenses()
            .groupBy(item => item.returnInspectionStepNumber);

    const dropDownCss: string = Utility.css(rentaToolsStyles.arsenalDropdown, (!ch.mobile && !manualInvoice()) && styles.desktopDropdownStyle, (manualInvoice() && !ch.mobile) && styles.newInvoiceDesktopDropdownStyle);


    return (
        <PageContainer alertClassName={rentaToolsStyles.alert} className={Utility.css(rentaToolsStyles.pageContainer, styles.returnInspectionInvoiceDetails)}>

            <PageHeader title={() => renderTitle()} className={Utility.css(rentaToolsStyles.leftPageHeader)}>

                {
                    (!manualInvoice()) && (
                        <div className={styles.deviceActions}>

                            <ArsenalButton block secret label={Localizer.invoiceDetailsPageButtonLabelPreview}
                                           className={Utility.css(rentaToolsStyles.arsenalButton, styles.infoButton)}
                                           onClick={() => previewReturnInspectionAsync()}
                            />

                        </div>
                    )
                }


            </PageHeader>

            <ArsenalPageRow>

                <div className="col">

                    <div className={styles.container}>

                        {
                            (hasData()) &&
                            (
                                <React.Fragment>

                                    <div>

                                        <table id="invoiceDetails" className={Utility.css(styles.table, "table table-striped")}>

                                            <tbody>
                                            {
                                                <tr>
                                                    <td>{Localizer.invoiceDetailsPageCreatedAt}</td>
                                                    <td>{ToolsUtility.toDateString(invoice?.createdAt)}</td>
                                                </tr>
                                            }

                                            {
                                                (!manualInvoice() && report()) && (
                                                    <>
                                                        <tr>
                                                            <td>{Localizer.invoiceDetailsPageInspectedAt}</td>
                                                            <td>{inspectedAt}</td>
                                                        </tr>
                                                        <tr>
                                                            <td>{Localizer.invoiceDetailsPageInspectedBy}</td>
                                                            <td>{"{0}".format(report()!.user)}</td>
                                                        </tr>
                                                        <tr>
                                                            <td>{Localizer.invoiceDetailsPageDepo}</td>
                                                            <td>{"{0}".format(report()!.depo)}</td>
                                                        </tr>
                                                        {
                                                            (returnInspectionInvoice()) &&
                                                            (
                                                                <tr>
                                                                    <td>{Localizer.invoiceDetailsPageInspectionResult}</td>
                                                                    <td>
                                                                        {
                                                                            ((report() as Report).passed)
                                                                                    ? Localizer.invoiceDetailsPageInspectionPassed
                                                                                    : Localizer.invoiceDetailsPageInspectionFailed
                                                                        }
                                                                    </td>
                                                                </tr>
                                                            )
                                                        }
                                                    </>
                                                )
                                            }

                                            {
                                                (deviceContract() != null) && (
                                                    <>
                                                        <tr>
                                                            <td>{Localizer.invoiceDetailsPageCustomerName}</td>
                                                            <td>{"{0}".format(deviceContract()!.customerName || "-")}</td>
                                                        </tr>
                                                        <tr>
                                                            <td>{Localizer.invoiceDetailsPageConstructionSite}</td>
                                                            <td>{"{0}".format(deviceContract()!.constructionSiteName || "-")}</td>
                                                        </tr>
                                                        <tr>
                                                            <td>{Localizer.invoiceDetailsPageContractId}</td>
                                                            <td>{"{0}".format(deviceContract()!.contractExternalId)}</td>
                                                        </tr>
                                                        <tr>
                                                            <td>{Localizer.invoiceDetailsPageConstructionSiteNumber}</td>
                                                            <td>{deviceContract()!.constructionSiteExternalId ? "{0}".format(deviceContract()!.constructionSiteExternalId) : "-"}</td>
                                                        </tr>
                                                    </>
                                                )
                                            }

                                            {
                                                (!!invoice && invoice.approved) &&
                                                (
                                                    <React.Fragment>
                                                        <tr>
                                                            <td>{Localizer.invoiceDetailsPageApprovedAt}</td>
                                                            <td>{ToolsUtility.toDateString(invoice.approvedAt)}</td>
                                                        </tr>
                                                        <tr>
                                                            <td>{Localizer.invoiceDetailsPageApprovedBy}</td>
                                                            <td>{"{0}".format(invoice.approvedBy)}</td>
                                                        </tr>
                                                        <tr>
                                                            <td>{Localizer.invoiceDetailsPageComment}</td>
                                                            <td>{"{0}".format(invoice.comment)}</td>
                                                        </tr>
                                                    </React.Fragment>
                                                )
                                            }
                                            {
                                                (!!invoice && invoice.declined) &&
                                                (
                                                    <React.Fragment>
                                                        <tr>
                                                            <td>{Localizer.invoiceDetailsPageDeclinedAt}</td>
                                                            <td>{ToolsUtility.toDateString(invoice.declinedAt)}</td>
                                                        </tr>
                                                        <tr>
                                                            <td>{Localizer.invoiceDetailsPageDeclinedBy}</td>
                                                            <td>{"{0}".format(invoice.declinedBy)}</td>
                                                        </tr>
                                                        <tr>
                                                            <td>{Localizer.invoiceDetailsPageComment}</td>
                                                            <td>{"{0}".format(invoice.comment)}</td>
                                                        </tr>
                                                    </React.Fragment>
                                                )
                                            }
                                            </tbody>
                                        </table>

                                    </div>

                                    <div id={"statusInfo"}
                                         className={styles.info}>
                                        <p>
                                            {Localizer.invoiceDetailsPageInvoiceStatus}
                                            <span>{statusLabel()}</span>
                                        </p>
                                    </div>

                                    {
                                        (manualInvoice()) && (
                                            <Dropdown required
                                                      id={"deviceContractId"}
                                                      className={dropDownCss}
                                                      label={Localizer.invoiceDetailsPageContractId}
                                                      items={deviceContracts}
                                                      selectedItem={selectedDeviceContractIdForManualInvoice()}
                                                      disabled={readonly()}
                                                      onChange={(sender, value) => setContractIdAsync(value!)}
                                            />
                                        )
                                    }

                                    <br/>

                                    {
                                        (invoice) && invoice.invoiceExpenses.map(invoiceExpense => (
                                            <>
                                                <div id={`expense_${invoiceExpense.expenseId}`} className={manualInvoice() ? "" : styles.value} key={`expense_${invoiceExpense.expenseId}`}>

                                                    {
                                                        (!manualInvoice()) &&
                                                        (
                                                            <Checkbox inline
                                                                      id={`shouldInvoice_${invoiceExpense.expenseId}`}
                                                                      readonly={readonly() || !isExpenseInvoiceable(invoiceExpense)}
                                                                      inlineType={InlineType.Right}
                                                                      value={invoiceExpenseSelected(invoiceExpense)}
                                                                      onChange={(sender, value) => setShouldInvoiceExpense(invoiceExpense.expenseId!, value)}
                                                            />
                                                        )
                                                    }


                                                    <NumberWidget id={`setExpenseAmount_${invoiceExpense.expenseId}`}
                                                                  className={Utility.css(rentaToolsStyles.arsenalNumberWidget)}
                                                                  label={getExpenseLabel(invoiceExpense)}
                                                                  format={"0.0"}
                                                                  step={fuelStep() || 0.1}
                                                                  min={0}
                                                                  max={99999}
                                                                  readonly={readonly()}
                                                                  value={getInvoiceExpenseAmount(invoiceExpense) || 0}
                                                                  onChange={(sender, value) => setExpenseAmountAsync(invoiceExpense.expenseId!, value)}
                                                    />


                                                </div>

                                                <Dropdown required
                                                          id={`expenseTypes_${invoiceExpense.expenseId}`}
                                                          requiredType={DropdownRequiredType.Restricted}
                                                          className={dropDownCss}
                                                          label={getExpenseDropdownLabel(invoiceExpense)}
                                                          disabled={readonly()}
                                                          items={possibleExpenseTypes(invoiceExpense)}
                                                          selectedItem={selectedExpenseType(invoiceExpense) || undefined}
                                                          onChange={(sender, value) => setExpenseTypeAsync(invoiceExpense, value!)}
                                                />
                                            </>
                                        ))
                                    }

                                    <br/>

                                    {
                                        (manualInvoice() && isNewInvoice()) &&
                                        (
                                            <Checkbox inline
                                                      id={"invoiceOtherResourcesCheckbox"}
                                                      className={styles.invoiceOtherResourcesCheckbox}
                                                      label={Localizer.invoiceDetailsPageInvoiceOther}
                                                      inlineType={InlineType.Right}
                                                      value={invoiceOtherResources}
                                                      onChange={(sender, checked) => setInvoiceOtherResourcesAsync(checked)}
                                            />
                                        )
                                    }

                                    {
                                        (invoiceOtherResources && isNewInvoice()) &&
                                        (
                                            <>
                                                <br/>
                                                <Dropdown id={`expenses`}
                                                          label={Localizer.genericExpenses}
                                                          className={dropDownCss}
                                                          items={expenses}
                                                          onChange={(sender, value) => addExpenseToInvoiceAsync(value!)}
                                                />
                                            </>
                                        )
                                    }

                                    {
                                        (invoicedAdditionalExpenses().length > 0) &&
                                        (
                                            <>
                                                <p className={styles.additionalExpensesTitle}>{Localizer.invoiceDetailsPageAdditionalExpensesTitle}</p>

                                                {groupedAdditionalExpenses.map((item, index) => renderAdditionalExpenses(item, index))}
                                            </>
                                        )
                                    }

                                    <br/>
                                    <br/>

                                    {
                                        (manualInvoice() && isNewInvoice()) && (
                                            <ArsenalButton block big
                                                           type={ButtonType.Orange}
                                                           className={rentaToolsStyles.arsenalButton}
                                                           icon={{name: "far save"}}
                                                           label={Localizer.genericSave}
                                                           disabled={readonly() || !canApprove() || noContractSelected()}
                                                           confirm={{title: Localizer.invoiceDetailsPageModalApproveInvoice, comment: false}}
                                                           onClick={(_, data) => processInvoiceAsync(true, false, data)}
                                            />
                                        )
                                    }

                                    {
                                        (!isNewInvoice()) && (
                                            <>
                                                <ArsenalButton block big
                                                               id={"approveInvoice"}
                                                               type={ButtonType.Success}
                                                               className={rentaToolsStyles.arsenalButton}
                                                               icon={{name: "far thumbs-up"}}
                                                               label={Localizer.invoiceDetailsPageButtonLabelApprove}
                                                               disabled={readonly() || !canApprove()}
                                                               confirm={{title: Localizer.invoiceDetailsPageModalApproveInvoice, comment: false}}
                                                               onClick={(_, data) => processInvoiceAsync(true, false, data)}
                                                />

                                                <ArsenalButton block big
                                                               id={"declineInvoice"}
                                                               type={ButtonType.Blue}
                                                               className={rentaToolsStyles.arsenalButton}
                                                               icon={{name: "far thumbs-down"}}
                                                               label={Localizer.invoiceDetailsPageButtonLabelDecline}
                                                               disabled={readonly() || !canReject()}
                                                               confirm={{title: Localizer.invoiceDetailsPageModalDeclineInvoice, comment: true}}
                                                               onClick={(sender, data) => processInvoiceAsync(false, true, data)}
                                                />
                                            </>
                                        )
                                    }

                                    <ArsenalButton block
                                                   className={rentaToolsStyles.arsenalButton}
                                                   type={ButtonType.Orange}
                                                   label={Localizer.devicePageToDevicePage}
                                                   onClick={toDevicePage}/>

                                    <ArsenalButton block big
                                                   type={ButtonType.Orange}
                                                   className={rentaToolsStyles.arsenalButton}
                                                   label={Localizer.devicePageReturnBack}
                                                   onClick={returnBackAsync}
                                    />

                                </React.Fragment>
                            )
                        }

                    </div>

                </div>

            </ArsenalPageRow>

        </PageContainer>
    )
}

export default InvoiceDetails;